import { UploadDropzone } from '@atlas/ui-components'
import { fileTypeToken, formatBytes } from '@atlas/ui-domain'
import { DialogContent, DialogTitle, ProgressBar } from '@atlas/ui-primitives'
import { useEffect, useId, useState } from 'react'
import type { CampaignUploadInput, CampaignUploadOutcome, CampaignUploadTransientState } from '@/domain/campaign-ports'
import { CAMPAIGN_UPLOAD_FAILURE_COPY, isFileScopedUploadFailure } from './campaign-upload-failure-copy'
import {
	CAMPAIGN_UPLOAD_ACCEPT,
	CAMPAIGN_UPLOAD_GUIDANCE,
	type CampaignUploadSelection,
	validateCampaignUploadSelection,
} from './campaign-upload-selection'

export interface CampaignUploadDialogProps {
	isOpen: boolean
	transientState: CampaignUploadTransientState | 'idle'
	outcome?: CampaignUploadOutcome
	isPending: boolean
	/** Byte-transfer progress, 0–100; `null` outside an active transfer. */
	progress?: number | null
	onStartUpload: (file: CampaignUploadInput) => void
	onRetryAssociation: (operationToken: string) => void
	/** Clears the container-owned transient state, outcome, and progress. Never opens, closes, starts, or aborts. */
	onResetUploadPresentation: () => void
	onCancel: () => void
}

/** The eight observable states, derived on render — never stored, so they cannot desynchronise from props. */
type Presentation =
	| 'INITIAL_SELECTION'
	| 'INVALID_SELECTION'
	| 'VALID_SELECTION'
	| 'TRANSFER_PENDING'
	| 'NON_TRANSFER_PENDING'
	| 'UPLOAD_FAILURE'
	| 'ASSOCIATION_FAILURE'
	| 'SUCCESS'

const TRANSIENT_LABEL: Record<CampaignUploadTransientState | 'idle', string> = {
	idle: 'Choose a File to upload.',
	'upload-pending': 'Uploading…',
	uploaded: 'File uploaded — associating with this Campaign…',
	'association-pending': 'Associating with this Campaign…',
	'association-retry-pending': 'Retrying association…',
}

const ASSOCIATION_ALERT = 'The File uploaded, but associating it with this Campaign failed.'

function isPendingPresentation(presentation: Presentation): boolean {
	return presentation === 'TRANSFER_PENDING' || presentation === 'NON_TRANSFER_PENDING'
}

/**
 * Pending outranks a terminal outcome: during an association retry the container
 * still holds the preceding `association-failed` outcome, so checking the outcome
 * first would render that stale failure and leave the retry phase unreachable.
 * `TRANSFER_PENDING` also requires a numeric percentage, so the window before the
 * first progress callback reads as indeterminate rather than as `0%`. Terminal
 * outcomes outrank selection, so a local change alone can never bury a failure.
 */
function derivePresentation(
	props: Pick<CampaignUploadDialogProps, 'isPending' | 'transientState' | 'progress' | 'outcome'>,
	selection: CampaignUploadSelection | null,
): Presentation {
	if (props.isPending) {
		const isTransferring = props.transientState === 'upload-pending' && typeof props.progress === 'number'
		return isTransferring ? 'TRANSFER_PENDING' : 'NON_TRANSFER_PENDING'
	}
	if (props.outcome?.kind === 'association-failed') return 'ASSOCIATION_FAILURE'
	if (props.outcome?.kind === 'upload-failed') return 'UPLOAD_FAILURE'
	if (props.outcome?.kind === 'success') return 'SUCCESS'
	if (selection?.status === 'rejected') return 'INVALID_SELECTION'
	if (selection?.status === 'valid') return 'VALID_SELECTION'
	return 'INITIAL_SELECTION'
}

/** Exactly one of the two is ever non-null, so a status and a failure never coexist. */
function presentationCopy(
	presentation: Presentation,
	props: Pick<CampaignUploadDialogProps, 'transientState' | 'progress' | 'outcome'>,
	selection: CampaignUploadSelection | null,
): { status: string | null; failure: string | null } {
	const { transientState, progress, outcome } = props
	if (presentation === 'TRANSFER_PENDING') return { status: `Uploading… ${progress}%`, failure: null }
	if (presentation === 'NON_TRANSFER_PENDING') {
		// Before the first transient callback the container still reports `idle`, which
		// must not read as the pre-selection prompt while bytes are already moving.
		const label = transientState === 'idle' ? TRANSIENT_LABEL['upload-pending'] : TRANSIENT_LABEL[transientState]
		return { status: label, failure: null }
	}
	if (presentation === 'SUCCESS') return { status: 'Uploaded and associated with this Campaign.', failure: null }
	if (presentation === 'ASSOCIATION_FAILURE') return { status: null, failure: ASSOCIATION_ALERT }
	if (presentation === 'UPLOAD_FAILURE' && outcome?.kind === 'upload-failed') {
		return { status: null, failure: CAMPAIGN_UPLOAD_FAILURE_COPY[outcome.reason] }
	}
	if (presentation === 'INVALID_SELECTION' && selection?.status === 'rejected') {
		return { status: null, failure: CAMPAIGN_UPLOAD_FAILURE_COPY[selection.reason] }
	}
	return { status: TRANSIENT_LABEL.idle, failure: null }
}

/**
 * Only a failure about the staged File itself joins the input's description; a
 * permission or availability failure stands alone, and an association failure
 * concerns the association rather than the control.
 */
function isInputScopedFailure(presentation: Presentation, outcome: CampaignUploadOutcome | undefined): boolean {
	if (presentation === 'INVALID_SELECTION') return true
	const isFileScoped = outcome?.kind === 'upload-failed' && isFileScopedUploadFailure(outcome.reason)
	return presentation === 'UPLOAD_FAILURE' && isFileScoped
}

/** The staged File's identity — static text, never a live region, and never a preview of any kind. */
function SelectedFileSummary({ file, onRemove }: { file: File; onRemove: () => void }) {
	return (
		<div className="flex items-center gap-3 rounded-ds-md border border-ds-border bg-ds-surface p-3">
			<div className="min-w-0 flex-1">
				<p title={file.name} className="min-w-0 truncate text-sm font-medium text-ds-text">
					{file.name}
				</p>
				<p className="text-xs text-ds-text-muted">
					{fileTypeToken(file.type, file.name)} · {formatBytes(file.size)}
				</p>
			</div>
			{/*
			 * The file name is the accessible name only, never the visible label:
			 * rendering it inside a `shrink-0` button let a long name push the row
			 * wider than the dialog instead of truncating with the name above it.
			 */}
			<button
				type="button"
				onClick={onRemove}
				aria-label={`Remove ${file.name}`}
				className="shrink-0 rounded-ds-md border border-ds-border px-3 py-1.5 text-xs text-ds-text hover:bg-ds-surface-elevated"
			>
				Remove
			</button>
		</div>
	)
}

/** The scrolling region: guidance, selection surface, summary, progress, and the one status or alert. */
function UploadDialogBody(props: {
	presentation: Presentation
	ids: { guidance: string; alert: string }
	dialog: CampaignUploadDialogProps
	selection: CampaignUploadSelection | null
	isSelectionLocked: boolean
	stagedFile: File | null
	onSelectFile: (file: File) => void
	onRemove: () => void
}) {
	const { presentation, ids, dialog, selection, isSelectionLocked, stagedFile } = props
	const { status, failure } = presentationCopy(presentation, dialog, selection)
	const uploadedFileName = dialog.outcome?.kind === 'association-failed' ? dialog.outcome.fileName : null
	const describedBy = isInputScopedFailure(presentation, dialog.outcome) ? `${ids.guidance} ${ids.alert}` : ids.guidance

	return (
		<div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
			<p id={ids.guidance} className="text-xs text-ds-text-muted">
				{CAMPAIGN_UPLOAD_GUIDANCE}
			</p>

			<UploadDropzone
				label="Drop a File here, or choose one"
				accept={CAMPAIGN_UPLOAD_ACCEPT}
				// One asset per dialog: the flow uploads a single File and associates
				// it, so multi-select would leave the caller to pick a winner.
				multiple={false}
				describedBy={describedBy}
				isInvalid={presentation === 'INVALID_SELECTION'}
				isDisabled={isSelectionLocked}
				// Only the first File is taken; any remainder is ignored
				// deterministically, with no extra rejection vocabulary for it.
				onFiles={(files) => props.onSelectFile(files[0])}
			/>

			{stagedFile !== null && !isSelectionLocked && <SelectedFileSummary file={stagedFile} onRemove={props.onRemove} />}

			{uploadedFileName !== null && (
				<p className="min-w-0 break-words text-sm text-ds-text">
					{`${uploadedFileName} is stored and was not deleted. Retry the association, or close this dialog and open a new one to upload a different File.`}
				</p>
			)}

			{presentation === 'TRANSFER_PENDING' && (
				<ProgressBar value={Math.min(100, Math.max(0, dialog.progress ?? 0))} aria-label="Upload progress" />
			)}
			{presentation === 'NON_TRANSFER_PENDING' && <ProgressBar isIndeterminate aria-label="Upload progress" />}

			{status !== null && (
				<p role="status" className="text-sm text-ds-text-muted">
					{status}
				</p>
			)}
			{failure !== null && (
				<p id={ids.alert} role="alert" className="text-sm text-ds-danger">
					{failure}
				</p>
			)}
		</div>
	)
}

/** Wraps rather than overflows at narrow widths. Every control exposes its disabled state natively. */
function UploadDialogFooter(props: {
	presentation: Presentation
	dialog: CampaignUploadDialogProps
	stagedFile: File | null
}) {
	const { presentation, dialog, stagedFile } = props
	const isPendingState = isPendingPresentation(presentation)
	const token = dialog.outcome?.kind === 'association-failed' ? dialog.outcome.operationToken : null
	const secondary = 'rounded-ds-md border border-ds-border px-4 py-2 text-sm text-ds-text hover:bg-ds-surface'
	const primary =
		'rounded-ds-md bg-ds-primary px-4 py-2 text-sm text-ds-primary-foreground hover:opacity-90 disabled:opacity-50'

	return (
		<div className="flex shrink-0 flex-wrap justify-end gap-3 p-6 pt-0">
			<button
				type="button"
				onClick={dialog.onCancel}
				disabled={isPendingState}
				className={`${secondary} disabled:opacity-50`}
			>
				Close
			</button>
			{presentation === 'SUCCESS' && (
				<button type="button" onClick={dialog.onResetUploadPresentation} className={secondary}>
					Upload another File
				</button>
			)}
			{token !== null ? (
				<button
					type="button"
					onClick={() => dialog.onRetryAssociation(token)}
					disabled={isPendingState}
					className={primary}
				>
					Retry association
				</button>
			) : (
				<button
					type="button"
					onClick={() =>
						stagedFile &&
						dialog.onStartUpload({
							name: stagedFile.name,
							contentType: stagedFile.type,
							size: stagedFile.size,
							bytes: stagedFile,
						})
					}
					disabled={isPendingState || stagedFile === null || presentation === 'SUCCESS'}
					className={primary}
				>
					Upload
				</button>
			)}
		</div>
	)
}

/**
 * The dialog's only local state. The selection is cleared on session end — the
 * component hides rather than unmounts, so a reopened dialog would otherwise
 * present a stale File — and once the File is confirmed server-side.
 * `upload-failed` deliberately keeps it: nothing landed, so the same File
 * remains useful for resubmission or replacement.
 */
function useUploadDialogLocalState(props: CampaignUploadDialogProps) {
	const { isOpen } = props
	const outcomeKind = props.outcome?.kind
	const [selection, setSelection] = useState<CampaignUploadSelection | null>(null)

	useEffect(() => {
		if (!isOpen) setSelection(null)
	}, [isOpen])

	useEffect(() => {
		if (outcomeKind === 'success' || outcomeKind === 'association-failed') setSelection(null)
	}, [outcomeKind])

	return [selection, setSelection] as const
}

/**
 * Upload presentation over the single opaque upload-and-associate capability.
 * The page invokes exactly one operation for the whole flow, plus the association
 * retry; this dialog never separately drives a start, transfer, confirm, or
 * associate sequence, and owns no server state — only the local validation
 * verdict for a staged File.
 */
export function CampaignUploadDialog(props: CampaignUploadDialogProps) {
	const { isOpen, isPending, outcome, onCancel, onResetUploadPresentation } = props
	const descriptionId = useId()
	const guidanceId = useId()
	const alertId = useId()
	const [selection, setSelection] = useUploadDialogLocalState(props)

	if (!isOpen) return null

	const presentation = derivePresentation(props, selection)
	const isSelectionLocked = isPendingPresentation(presentation) || presentation === 'ASSOCIATION_FAILURE'
	const stagedFile = selection?.status === 'valid' ? selection.file : null

	function selectFile(file: File) {
		if (isSelectionLocked) return
		// A terminal outcome is container-owned and outranks selection, so it must be
		// cleared before the new verdict lands or it would render over the replacement.
		if (outcome !== undefined) onResetUploadPresentation()
		setSelection(validateCampaignUploadSelection(file))
	}

	function removeSelection() {
		if (outcome?.kind === 'upload-failed') onResetUploadPresentation()
		setSelection(null)
	}

	return (
		<DialogContent
			size="lg"
			isOpen={isOpen}
			onOpenChange={(next) => {
				if (!next) onCancel()
			}}
			isKeyboardDismissDisabled={isPending}
			aria-describedby={descriptionId}
			className="flex flex-col overflow-hidden p-0"
		>
			<div className="shrink-0 p-6 pb-0">
				<DialogTitle>Upload a new File</DialogTitle>
				<p id={descriptionId} className="mt-2 text-sm text-ds-text-muted">
					Upload one File and associate it with this Campaign.
				</p>
			</div>

			<UploadDialogBody
				presentation={presentation}
				ids={{ guidance: guidanceId, alert: alertId }}
				dialog={props}
				selection={selection}
				isSelectionLocked={isSelectionLocked}
				stagedFile={stagedFile}
				onSelectFile={selectFile}
				onRemove={removeSelection}
			/>

			<UploadDialogFooter presentation={presentation} dialog={props} stagedFile={stagedFile} />
		</DialogContent>
	)
}
